import { useState, useEffect, useRef } from 'react';
import {
  Title, Text, Stack, Badge, Alert, Loader, Center,
  Group, Select, Button, Paper, ScrollArea, Code, SimpleGrid, Tooltip,
  TextInput, Checkbox, Divider, SegmentedControl, Accordion,
  Radio, Pagination,
} from '@mantine/core';
import { IconAlertCircle, IconDeviceFloppy, IconPlayerPlay, IconRefresh, IconX } from '@tabler/icons-react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { api } from '../../apiClient';

const SCRAPER_API_URL = process.env.REACT_APP_SCRAPER_API_URL || 'http://localhost:8000';

const JOB_TYPE_LABELS = {
  discussions:      'Discussions 取得',
  questions:        '問題スクレイピング',
  classify:         'カテゴリ/タスク判定',
  rescrape:         '単体問題の再処理',
  scheduled_update: '定期更新',
};

const STATUS_LABELS = {
  running:   '実行中',
  completed: '完了',
  failed:    '失敗',
  pending:   '待機中',
};

const statusColor = (status) => {
  switch (status) {
    case 'running':   return 'blue';
    case 'completed': return 'green';
    case 'failed':    return 'red';
    case 'pending':   return 'yellow';
    default:          return 'gray';
  }
};

const formatDate = (iso) => {
  if (!iso) return '-';
  return new Date(iso).toLocaleString('ja-JP', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
};

// ジョブのログ表示パネル（アコーディオン展開時）
function JobLogPanel({ job, onCancel }) {
  const viewportRef = useRef(null);
  const logs = job.logs ?? [];

  useEffect(() => {
    if (viewportRef.current) {
      viewportRef.current.scrollTo({ top: viewportRef.current.scrollHeight });
    }
  }, [logs.length]);

  return (
    <Stack gap="xs">
      <Group justify="space-between">
        <Text size="xs" c="dimmed">
          開始: {formatDate(job.started_at)}
          {job.finished_at && `　完了: ${formatDate(job.finished_at)}`}
        </Text>
        {job.status === 'running' && (
          <Tooltip label="ジョブを failed に変更する" withArrow>
            <Button
              size="xs"
              variant="subtle"
              color="red"
              leftSection={<IconX size={12} />}
              onClick={() => onCancel(job.id)}
            >
              キャンセル
            </Button>
          </Tooltip>
        )}
      </Group>
      {job.error_message && (
        <Text size="xs" c="red">エラー: {job.error_message}</Text>
      )}
      <ScrollArea h={300} viewportRef={viewportRef}>
        <Code block style={{ whiteSpace: 'pre-wrap', fontSize: 11 }}>
          {logs.length > 0 ? logs.join('\n') : '（ログなし）'}
          {job.status === 'running' && '\n▌'}
        </Code>
      </ScrollArea>
    </Stack>
  );
}

function ScrapeControl() {
  // ── ジョブ管理 ────────────────────────────────────────────────────────────
  // { [job_id]: { id, job_type, vendor, certCode, status, processed, total, logs[], started_at, finished_at, error_message, created_at } }
  const [jobsMap, setJobsMap]         = useState({});
  const [pageJobIds, setPageJobIds]   = useState([]); // 現在のページのジョブ ID 順序
  const [loadingJobs, setLoadingJobs] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalJobCount, setTotalJobCount] = useState(0);
  const [openedJobs, setOpenedJobs]   = useState([]); // 展開中のアコーディオン item

  // ── discussions 件数 ──────────────────────────────────────────────────────
  const [counts, setCounts]           = useState({ aws: null, microsoft: null, google: null });
  const [loadingCounts, setLoadingCounts] = useState(true);

  // ── 資格一覧（クイック選択用） ────────────────────────────────────────────
  const [certifications, setCertifications] = useState([]);
  const [certVendorFilter, setCertVendorFilter] = useState('all');

  // ── ジョブ起動フォーム ────────────────────────────────────────────────────
  const [formJobType,          setFormJobType]          = useState('discussions');
  const [formVendor,           setFormVendor]           = useState('aws');
  const [formCertCodes,        setFormCertCodes]        = useState([]);  // 複数選択
  const [formQuestionsMode,    setFormQuestionsMode]    = useState('scrape_only');
  const [formForce,            setFormForce]            = useState(false);
  const [isStarting,           setIsStarting]           = useState(false);

  // ── エラー ────────────────────────────────────────────────────────────────
  const [error, setError] = useState(null);

  // ── 定期更新 ──────────────────────────────────────────────────────────────
  const [isScheduledUpdating, setIsScheduledUpdating] = useState(false);
  const [scheduledUpdateError, setScheduledUpdateError] = useState(null);
  const [scheduledUpdateSuccess, setScheduledUpdateSuccess] = useState(false);

  // ── 単体問題の再処理 ──────────────────────────────────────────────────────
  const [rescrapeQuestionId, setRescrapeQuestionId] = useState('');
  const [rescrapeMode,       setRescrapeMode]       = useState('full');
  const [isRescraping,       setIsRescraping]       = useState(false);
  const [rescrapeError,      setRescrapeError]      = useState(null);
  const [rescrapeSuccess,    setRescrapeSuccess]    = useState(false);

  // ── Bedrock 設定 ──────────────────────────────────────────────────────────
  const [bedrockSettings, setBedrockSettings] = useState({ bedrock_region: '', bedrock_model_id: '' });
  const [bedrockSaving,   setBedrockSaving]   = useState(false);
  const [bedrockError,    setBedrockError]    = useState(null);
  const [bedrockSuccess,  setBedrockSuccess]  = useState(false);

  // ── 内部 refs ─────────────────────────────────────────────────────────────
  const activeStreams    = useRef(new Set()); // 接続中の SSE job_id セット
  const fetchJobsRef    = useRef(null);
  const fetchCountsRef  = useRef(null);
  const streamJobLogsRef = useRef(null);

  // ── state ヘルパー（functional update で stale closure 回避） ──────────────
  const updateJob = (jobId, updates) => {
    setJobsMap((prev) => {
      if (!prev[jobId]) return prev;
      return { ...prev, [jobId]: { ...prev[jobId], ...updates } };
    });
  };

  const appendJobLog = (jobId, line) => {
    setJobsMap((prev) => {
      if (!prev[jobId]) return prev;
      return { ...prev, [jobId]: { ...prev[jobId], logs: [...(prev[jobId].logs ?? []), line] } };
    });
  };

  // ── 共通 SSE ストリーミング ────────────────────────────────────────────────
  const streamJobLogs = async (jobId, token) => {
    if (activeStreams.current.has(jobId)) return;
    activeStreams.current.add(jobId);

    const MAX_RETRIES = 20;
    let retries = 0;

    while (retries <= MAX_RETRIES) {
      try {
        const res = await fetch(`${SCRAPER_API_URL}/scraper/logs/${jobId}`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (!res.ok) throw new Error('ログの取得に失敗しました');

        const reader  = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop();
          for (const line of lines) {
            if (line.startsWith(':') || !line.startsWith('data: ')) continue;
            try {
              const data = JSON.parse(line.slice(6));
              if (data.line !== undefined) appendJobLog(jobId, data.line);
              if (data.done) {
                updateJob(jobId, { status: 'completed' });
                activeStreams.current.delete(jobId);
                fetchJobsRef.current?.();
                fetchCountsRef.current?.();
                return;
              }
              if (data.error) {
                updateJob(jobId, { status: 'failed', error_message: data.error });
                activeStreams.current.delete(jobId);
                return;
              }
            } catch (_) {}
          }
        }

        // ストリーム終了 → ジョブ状態確認
        const jobStatus = await api.get('/scrape-jobs', { id: jobId }).catch(() => null);
        if (!jobStatus || jobStatus.status !== 'running') {
          updateJob(jobId, { status: jobStatus?.status ?? 'completed' });
          activeStreams.current.delete(jobId);
          fetchJobsRef.current?.();
          fetchCountsRef.current?.();
          return;
        }

        retries++;
        appendJobLog(jobId, `--- 再接続中... (${retries}/${MAX_RETRIES}) ---`);
        await new Promise((r) => setTimeout(r, 3000));

      } catch (err) {
        if (err.name === 'AbortError') {
          updateJob(jobId, { status: 'failed' });
          activeStreams.current.delete(jobId);
          return;
        }
        const jobStatus = await api.get('/scrape-jobs', { id: jobId }).catch(() => null);
        if (!jobStatus || jobStatus.status !== 'running') {
          updateJob(jobId, { status: jobStatus?.status ?? 'failed' });
          activeStreams.current.delete(jobId);
          fetchJobsRef.current?.();
          fetchCountsRef.current?.();
          return;
        }
        retries++;
        appendJobLog(jobId, `--- 接続エラー。再接続中... (${retries}/${MAX_RETRIES}) ---`);
        await new Promise((r) => setTimeout(r, 3000));
      }
    }

    appendJobLog(jobId, '--- ログ接続を終了しました ---');
    updateJob(jobId, { status: 'completed' });
    activeStreams.current.delete(jobId);
    fetchJobsRef.current?.();
  };

  streamJobLogsRef.current = streamJobLogs;

  // ── API 取得関数 ──────────────────────────────────────────────────────────
  const fetchJobs = async (page) => {
    const targetPage = page ?? currentPage;
    setLoadingJobs(true);
    try {
      const data = await api.get('/scrape-jobs', { page: targetPage });
      const jobs       = data?.jobs       ?? [];
      const totalCount = data?.total_count ?? 0;
      setJobsMap((prev) => {
        const next = { ...prev };
        for (const job of jobs) {
          next[job.id] = {
            ...job,
            certCode: prev[job.id]?.certCode ?? '',
            logs:     prev[job.id]?.logs     ?? [],
          };
        }
        return next;
      });
      setPageJobIds(jobs.map((j) => j.id));
      setTotalJobCount(totalCount);
    } catch (err) {
      console.error('scrape-jobs 取得失敗:', err);
    } finally {
      setLoadingJobs(false);
    }
  };
  fetchJobsRef.current = fetchJobs;

  const fetchCounts = async () => {
    setLoadingCounts(true);
    try {
      const [awsRes, msRes, googleRes] = await Promise.all([
        api.get('/discussions/count', { vendor: 'aws' }),
        api.get('/discussions/count', { vendor: 'microsoft' }),
        api.get('/discussions/count', { vendor: 'google' }),
      ]);
      setCounts({ aws: awsRes.count ?? 0, microsoft: msRes.count ?? 0, google: googleRes.count ?? 0 });
    } catch (err) {
      console.error('discussions 件数取得失敗:', err);
    } finally {
      setLoadingCounts(false);
    }
  };
  fetchCountsRef.current = fetchCounts;

  const fetchCertifications = async () => {
    try {
      const data = await api.get('/certifications');
      const sorted = [...(data ?? [])].sort((a, b) =>
        a.vendor.localeCompare(b.vendor) || a.code.localeCompare(b.code)
      );
      setCertifications(sorted);
    } catch (err) {
      console.error('資格一覧取得失敗:', err);
    }
  };

  const fetchBedrockSettings = async () => {
    try {
      const data = await api.get('/admin/settings');
      setBedrockSettings({
        bedrock_region:   data.bedrock_region   ?? '',
        bedrock_model_id: data.bedrock_model_id ?? '',
      });
    } catch (err) {
      console.error('Bedrock 設定取得失敗:', err);
    }
  };

  const handleBedrockSave = async () => {
    setBedrockSaving(true);
    setBedrockError(null);
    setBedrockSuccess(false);
    try {
      await api.put('/admin/settings', { key: 'bedrock_region',   value: bedrockSettings.bedrock_region });
      await api.put('/admin/settings', { key: 'bedrock_model_id', value: bedrockSettings.bedrock_model_id });
      setBedrockSuccess(true);
      setTimeout(() => setBedrockSuccess(false), 3000);
    } catch (err) {
      setBedrockError('設定の保存に失敗しました。');
    } finally {
      setBedrockSaving(false);
    }
  };

  // ── マウント時の初期化 ────────────────────────────────────────────────────
  useEffect(() => {
    const init = async () => {
      let runningJobs = [];
      try {
        const data   = await api.get('/scrape-jobs', { page: 1 });
        const jobs   = data?.jobs       ?? [];
        const total  = data?.total_count ?? 0;
        const newMap = {};
        for (const job of jobs) {
          newMap[job.id] = { ...job, certCode: '', logs: [] };
        }
        setJobsMap(newMap);
        setPageJobIds(jobs.map((j) => j.id));
        setTotalJobCount(total);
        runningJobs = jobs.filter((j) => j.status === 'running');
      } catch (e) {
        console.error('初期ジョブ取得失敗:', e);
      } finally {
        setLoadingJobs(false);
      }

      // running ジョブに自動で SSE 再接続
      if (runningJobs.length > 0) {
        try {
          const { tokens } = await fetchAuthSession();
          const token = tokens?.idToken?.toString();
          if (token) {
            runningJobs.forEach((job) => streamJobLogsRef.current?.(job.id, token));
          }
        } catch (e) {
          console.error('SSE 自動接続失敗:', e);
        }
      }
    };

    init();
    fetchCounts();
    fetchCertifications();
    fetchBedrockSettings();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── ジョブ操作 ────────────────────────────────────────────────────────────
  const handleCancelJob = async (jobId) => {
    try {
      await api.patch(`/scrape-jobs/${jobId}`, {
        status:        'failed',
        finished_at:   new Date().toISOString(),
        error_message: '手動キャンセル',
      });
      updateJob(jobId, { status: 'failed', error_message: '手動キャンセル' });
    } catch (err) {
      setError(`ジョブのキャンセルに失敗しました: ${err.message}`);
    }
  };

  // 複数選択のトグル
  const toggleCertCode = (code) => {
    setFormCertCodes((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    );
  };

  // 重複起動チェック
  const isAlreadyRunning = () => {
    return Object.values(jobsMap).some((j) => {
      if (j.status !== 'running' || j.job_type !== formJobType) return false;
      if (formJobType === 'discussions') return j.vendor === formVendor;
      if (formJobType === 'questions') {
        if (formCertCodes.length > 0) return formCertCodes.some((code) => j.certCode === code);
        return (j.vendor ?? '') === (formVendor ?? '') && !j.certCode;
      }
      if (formJobType === 'classify') return formCertCodes.some((code) => j.certCode === code);
      return false;
    });
  };

  const handleFormJobTypeChange = (v) => {
    setFormJobType(v);
    setFormCertCodes([]);
    setFormVendor(v === 'discussions' ? 'aws' : '');
    setFormForce(false);
  };

  // 1 ジョブ分の起動処理
  const startOneJob = async (token, certCode) => {
    let endpoint, reqBody;
    switch (formJobType) {
      case 'discussions':
        endpoint = '/scraper/start';
        reqBody  = { vendor: formVendor };
        break;
      case 'questions':
        endpoint = '/scraper/questions/start';
        reqBody  = {};
        switch (formQuestionsMode) {
          case 'scrape_translate':
            reqBody.translate = true;
            if (formForce) reqBody.force = true;
            break;
          case 'scrape_only':
            if (formForce) reqBody.force = true;
            break;
          case 'translate_html_only':
            reqBody.translate_html = true;
            break;
          case 'description_only':
            reqBody.update_description = true;
            break;
          case 'html_only':
            reqBody.update_html = true;
            break;
          default:
            break;
        }
        if (certCode)      reqBody.cert_code = certCode;
        else if (formVendor) reqBody.vendor  = formVendor;
        break;
      case 'classify':
        endpoint = '/scraper/classify/start';
        reqBody  = { cert_code: certCode };
        break;
      default:
        throw new Error('不明なジョブ種別です');
    }

    const res = await fetch(`${SCRAPER_API_URL}${endpoint}`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body:    JSON.stringify(reqBody),
    });

    if (!res.ok) {
      const errData = await res.json().catch(() => ({}));
      if (res.status === 409) throw new Error(`${certCode || formVendor || '全件'}: すでに実行中`);
      throw new Error(errData.detail || 'ジョブの起動に失敗しました');
    }

    const { job_id } = await res.json();

    const newJob = {
      id:          job_id,
      job_type:    formJobType,
      vendor:      formJobType === 'discussions'
                     ? formVendor
                     : (formJobType === 'questions' && !certCode ? (formVendor || null) : null),
      certCode:    certCode ?? '',
      status:      'running',
      processed:   0,
      total:       0,
      logs:        [],
      started_at:  new Date().toISOString(),
      finished_at: null,
      created_at:  new Date().toISOString(),
    };
    setJobsMap((prev) => ({ ...prev, [job_id]: newJob }));
    setOpenedJobs((prev) => [...prev, job_id]);
    streamJobLogsRef.current?.(job_id, token);
  };

  const handleStart = async () => {
    setError(null);
    setIsStarting(true);
    try {
      const { tokens } = await fetchAuthSession();
      const token = tokens?.idToken?.toString();
      if (!token) throw new Error('ログインが必要です');

      // 複数選択がある場合は並列起動、なければ単独起動
      const targets = formCertCodes.length > 0 ? formCertCodes : [null];
      const results = await Promise.allSettled(
        targets.map((code) => startOneJob(token, code))
      );

      const failures = results
        .filter((r) => r.status === 'rejected')
        .map((r) => r.reason?.message ?? '起動失敗');
      if (failures.length > 0) setError(failures.join(' / '));

      // ページ 1 に戻ってジョブ一覧を更新
      setCurrentPage(1);
      fetchJobs(1);

    } catch (err) {
      setError(err.message);
    } finally {
      setIsStarting(false);
    }
  };

  const handleRescrape = async () => {
    if (!rescrapeQuestionId || isNaN(Number(rescrapeQuestionId))) {
      setRescrapeError('有効な question_id（数値）を入力してください');
      return;
    }
    setIsRescraping(true);
    setRescrapeError(null);
    setRescrapeSuccess(false);
    try {
      const { tokens } = await fetchAuthSession();
      const token = tokens?.idToken?.toString();
      if (!token) throw new Error('ログインが必要です');

      const res = await fetch(`${SCRAPER_API_URL}/scraper/questions/rescrape`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body:    JSON.stringify({ question_id: Number(rescrapeQuestionId), mode: rescrapeMode }),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || '再処理の起動に失敗しました');
      }
      const { job_id } = await res.json();

      const newJob = {
        id:          job_id,
        job_type:    'rescrape',
        vendor:      rescrapeQuestionId,
        certCode:    '',
        status:      'running',
        processed:   0,
        total:       1,
        logs:        [],
        started_at:  new Date().toISOString(),
        finished_at: null,
        created_at:  new Date().toISOString(),
      };
      setJobsMap((prev) => ({ ...prev, [job_id]: newJob }));
      setOpenedJobs((prev) => [...prev, job_id]);
      streamJobLogsRef.current?.(job_id, token);
      setRescrapeSuccess(true);
      setTimeout(() => setRescrapeSuccess(false), 3000);
      setCurrentPage(1);
      fetchJobs(1);
    } catch (err) {
      setRescrapeError(err.message);
    } finally {
      setIsRescraping(false);
    }
  };

  const handleScheduledUpdate = async () => {
    setIsScheduledUpdating(true);
    setScheduledUpdateError(null);
    setScheduledUpdateSuccess(false);
    try {
      const { tokens } = await fetchAuthSession();
      const token = tokens?.idToken?.toString();
      if (!token) throw new Error('ログインが必要です');

      const res = await fetch(`${SCRAPER_API_URL}/scraper/update/start`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        if (res.status === 409) throw new Error('定期更新は既に実行中です');
        throw new Error(errData.detail || '起動に失敗しました');
      }
      const { job_id } = await res.json();

      const newJob = {
        id:          job_id,
        job_type:    'scheduled_update',
        vendor:      'all',
        certCode:    '',
        status:      'running',
        processed:   0,
        total:       0,
        logs:        [],
        started_at:  new Date().toISOString(),
        finished_at: null,
        created_at:  new Date().toISOString(),
      };
      setJobsMap((prev) => ({ ...prev, [job_id]: newJob }));
      setOpenedJobs((prev) => [...prev, job_id]);
      streamJobLogsRef.current?.(job_id, token);
      setScheduledUpdateSuccess(true);
      setTimeout(() => setScheduledUpdateSuccess(false), 3000);
      setCurrentPage(1);
      fetchJobs(1);
    } catch (err) {
      setScheduledUpdateError(err.message);
    } finally {
      setIsScheduledUpdating(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchJobs(page);
  };

  // ── 表示用データ ──────────────────────────────────────────────────────────
  // セッション起動ジョブ（ページ外）を先頭に追加して表示
  const sessionOnlyJobIds = Object.keys(jobsMap).filter(
    (id) => !pageJobIds.includes(id)
  );
  const displayJobs = [
    ...sessionOnlyJobIds.map((id) => jobsMap[id]).filter(Boolean),
    ...pageJobIds.map((id) => jobsMap[id]).filter(Boolean),
  ].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));

  const totalPages = Math.ceil(totalJobCount / 20);

  const startDisabled =
    isStarting ||
    isAlreadyRunning() ||
    (formJobType === 'classify' && formCertCodes.length === 0);

  const getJobTarget = (job) => {
    if (job.certCode) return job.certCode;
    if (job.vendor)   return job.vendor;
    if (job.job_type === 'questions') return '全件';
    return null;
  };

  // ── レンダリング ──────────────────────────────────────────────────────────
  return (
    <Stack gap="lg">
      <Group justify="space-between">
        <Title order={3}>スクレイピング管理</Title>
      </Group>

      {error && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          color="red"
          variant="light"
          withCloseButton
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {/* discussions 取得件数 */}
      <SimpleGrid cols={3}>
        {['aws', 'microsoft', 'google'].map((v) => (
          <Paper key={v} p="md" withBorder>
            <Text size="xs" c="dimmed" tt="uppercase" fw={600}>{v}</Text>
            <Text size="xl" fw={700} mt={4}>
              {loadingCounts ? <Loader size="xs" /> : (counts[v] ?? 0).toLocaleString()}
            </Text>
            <Text size="xs" c="dimmed">discussions 取得済み</Text>
          </Paper>
        ))}
      </SimpleGrid>

      {/* ジョブ起動フォーム */}
      <Paper p="md" withBorder>
        <Stack gap="md">
          <Text fw={500}>ジョブ起動</Text>

          {/* ジョブ種別 */}
          <Select
            label="ジョブ種別"
            value={formJobType}
            onChange={handleFormJobTypeChange}
            data={[
              { value: 'discussions', label: 'Discussions 取得' },
              { value: 'questions',   label: '問題スクレイピング' },
              { value: 'classify',    label: 'カテゴリ/タスク判定 (Bedrock)' },
            ]}
            w={300}
          />

          {/* Discussions: ベンダー選択 */}
          {formJobType === 'discussions' && (
            <Select
              label="ベンダー"
              value={formVendor}
              onChange={setFormVendor}
              data={[
                { value: 'aws',       label: 'AWS' },
                { value: 'microsoft', label: 'Microsoft' },
                { value: 'google',    label: 'Google' },
              ]}
              w={180}
            />
          )}

          {/* 問題スクレイピング */}
          {formJobType === 'questions' && (
            <Stack gap="sm">
              {/* 資格コード クイック選択（複数選択可） */}
              {certifications.length > 0 && (
                <Stack gap={4}>
                  <Group gap="xs" justify="space-between">
                    <Group gap={6}>
                      <Text size="xs" c="dimmed">資格コード選択</Text>
                      {formCertCodes.length > 0 && (
                        <Badge size="xs" variant="filled" color="teal" style={{ textTransform: 'none' }}>
                          {formCertCodes.length} 件選択中
                        </Badge>
                      )}
                    </Group>
                    <Group gap="xs">
                      {formCertCodes.length > 0 && (
                        <Button
                          size="xs"
                          variant="subtle"
                          color="gray"
                          onClick={() => setFormCertCodes([])}
                        >
                          選択解除
                        </Button>
                      )}
                      <SegmentedControl
                        size="xs"
                        value={certVendorFilter}
                        onChange={setCertVendorFilter}
                        data={[
                          { label: '全件',      value: 'all' },
                          { label: 'AWS',       value: 'aws' },
                          { label: 'Microsoft', value: 'microsoft' },
                          { label: 'Google',    value: 'google' },
                        ]}
                      />
                    </Group>
                  </Group>
                  <Group gap={6} wrap="wrap">
                    {certifications
                      .filter((c) => certVendorFilter === 'all' || c.vendor.toLowerCase() === certVendorFilter)
                      .map((cert) => (
                        <Tooltip key={cert.code} label={`${cert.name}  (ID: ${cert.id})`} withArrow>
                          <Badge
                            variant={formCertCodes.includes(cert.code) ? 'filled' : 'light'}
                            color={cert.vendor.toLowerCase() === 'aws' ? '#e8590c' : cert.vendor.toLowerCase() === 'google' ? '#34A853' : '#1c7ed6'}
                            style={{ cursor: 'pointer', textTransform: 'none' }}
                            onClick={() => toggleCertCode(cert.code)}
                          >
                            {cert.code}
                          </Badge>
                        </Tooltip>
                      ))}
                  </Group>
                </Stack>
              )}

              <Select
                label="ベンダー"
                description="資格コード未選択時に適用"
                value={formVendor}
                onChange={setFormVendor}
                data={[
                  { value: '',          label: '全件' },
                  { value: 'aws',       label: 'AWS' },
                  { value: 'microsoft', label: 'Microsoft' },
                  { value: 'google',    label: 'Google' },
                ]}
                disabled={formCertCodes.length > 0}
                w={180}
              />

              {/* モード選択 */}
              <Radio.Group
                value={formQuestionsMode}
                onChange={(v) => { setFormQuestionsMode(v); setFormForce(false); }}
                label="モード"
              >
                <Stack gap="xs" mt="xs">
                  <Radio
                    value="scrape_only"
                    label="問題・解答スクレイピングのみ"
                    description="新規問題を取得します（翻訳なし）"
                  />
                  <Radio
                    value="scrape_translate"
                    label="スクレイピング + 翻訳・解説生成"
                    description="取得後に Bedrock で日本語翻訳・解説を生成します"
                  />
                  <Radio
                    value="translate_html_only"
                    label="HTML 翻訳のみ"
                    description="bt_question の HTML を Bedrock で日本語翻訳し question に保存します（全問題対象）"
                  />
                  <Radio
                    value="description_only"
                    label="解説生成のみ"
                    description="既存問題の解説を Bedrock で再生成します（全問題対象・常に上書き）"
                  />
                  <Radio
                    value="html_only"
                    label="HTML補完"
                    description="bt_question が未設定の問題に HTML を補完します"
                  />
                </Stack>
              </Radio.Group>

              {/* 上書きオプション（スクレイピングモードのみ） */}
              {(formQuestionsMode === 'scrape_only' || formQuestionsMode === 'scrape_translate') && (
                <Checkbox
                  label="上書きする"
                  description="既存問題も再取得します（データを全件上書き）"
                  checked={formForce}
                  onChange={(e) => setFormForce(e.currentTarget.checked)}
                />
              )}
            </Stack>
          )}

          {/* カテゴリ/タスク判定 */}
          {formJobType === 'classify' && (
            <Stack gap="sm">
              <Text size="xs" c="dimmed">
                category / task が未設定の問題を Bedrock (Claude Sonnet 4.6) で判定します。AWS 資格のみ対応。
              </Text>
              {certifications.filter((c) => c.vendor.toLowerCase() === 'aws').length > 0 && (
                <Stack gap={4}>
                  <Group gap={6}>
                    <Text size="xs" c="dimmed">資格コード選択 (AWS・複数選択可)</Text>
                    {formCertCodes.length > 0 && (
                      <Badge size="xs" variant="filled" color="teal" style={{ textTransform: 'none' }}>
                        {formCertCodes.length} 件選択中
                      </Badge>
                    )}
                    {formCertCodes.length > 0 && (
                      <Button size="xs" variant="subtle" color="gray" onClick={() => setFormCertCodes([])}>
                        選択解除
                      </Button>
                    )}
                  </Group>
                  <Group gap={6} wrap="wrap">
                    {certifications
                      .filter((c) => c.vendor.toLowerCase() === 'aws')
                      .map((cert) => (
                        <Tooltip key={cert.code} label={`${cert.name}  (ID: ${cert.id})`} withArrow>
                          <Badge
                            variant={formCertCodes.includes(cert.code) ? 'filled' : 'light'}
                            color="orange"
                            style={{ cursor: 'pointer', textTransform: 'none' }}
                            onClick={() => toggleCertCode(cert.code)}
                          >
                            {cert.code}
                          </Badge>
                        </Tooltip>
                      ))}
                  </Group>
                </Stack>
              )}
            </Stack>
          )}

          {isAlreadyRunning() && (
            <Alert icon={<IconAlertCircle size={14} />} color="yellow" variant="light" py="xs">
              同じ対象のジョブがすでに実行中です
            </Alert>
          )}

          <Button
            leftSection={<IconPlayerPlay size={16} />}
            onClick={handleStart}
            loading={isStarting}
            disabled={startDisabled}
            w="fit-content"
          >
            {formCertCodes.length > 1 ? `${formCertCodes.length} 件を起動する` : '起動する'}
          </Button>
        </Stack>
      </Paper>

      {/* 単体問題の再処理 */}
      <Paper p="md" withBorder>
        <Stack gap="sm">
          <Text fw={500}>単体問題の再処理</Text>
          <Text size="xs" c="dimmed">
            question_id を指定して単体の問題を再スクレイピング・翻訳・解説生成します。
          </Text>
          {rescrapeError   && (
            <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light" withCloseButton onClose={() => setRescrapeError(null)}>
              {rescrapeError}
            </Alert>
          )}
          {rescrapeSuccess && <Alert color="green" variant="light">再処理ジョブを起動しました。ジョブ一覧で進捗を確認してください。</Alert>}
          <TextInput
            label="question_id"
            placeholder="例: 2010001"
            value={rescrapeQuestionId}
            onChange={(e) => setRescrapeQuestionId(e.currentTarget.value)}
            disabled={isRescraping}
            w={180}
          />
          <Radio.Group
            value={rescrapeMode}
            onChange={setRescrapeMode}
            label="モード"
          >
            <Stack gap="xs" mt="xs">
              <Radio
                value="full"
                label="すべて修正"
                description="再スクレイピング + 翻訳 + 解説生成"
              />
              <Radio
                value="scrape_translate"
                label="問題/解答のスクレイピング/翻訳"
                description="問題・解答を再取得して翻訳します"
              />
              <Radio
                value="translate_only"
                label="翻訳のみ"
                description="問題文を翻訳します"
              />
              <Radio
                value="description_only"
                label="解説生成のみ"
                description="解説を再生成します"
              />
            </Stack>
          </Radio.Group>
          <Button
            leftSection={<IconPlayerPlay size={16} />}
            onClick={handleRescrape}
            loading={isRescraping}
            disabled={isRescraping || !rescrapeQuestionId}
            w="fit-content"
          >
            実行
          </Button>
        </Stack>
      </Paper>

      {/* 定期更新 */}
      <Paper p="md" withBorder>
        <Stack gap="sm">
          <Text fw={500}>定期更新</Text>
          <Text size="xs" c="dimmed">
            両ベンダーのディスカッションを取得し、未登録の問題を資格ごとにスクレイピング・翻訳・解説生成します。
            毎週月曜 3:00 JST に自動実行されます。
          </Text>
          {scheduledUpdateError && (
            <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light" withCloseButton onClose={() => setScheduledUpdateError(null)}>
              {scheduledUpdateError}
            </Alert>
          )}
          {scheduledUpdateSuccess && (
            <Alert color="green" variant="light">定期更新ジョブを起動しました。ジョブ一覧で進捗を確認してください。</Alert>
          )}
          <Button
            leftSection={<IconPlayerPlay size={16} />}
            onClick={handleScheduledUpdate}
            loading={isScheduledUpdating}
            disabled={isScheduledUpdating}
            w="fit-content"
          >
            今すぐ実行
          </Button>
        </Stack>
      </Paper>

      {/* Bedrock 設定パネル（据え置き） */}
      <Paper p="md" withBorder>
        <Stack gap="sm">
          <Text fw={500}>Bedrock 設定</Text>
          <Text size="xs" c="dimmed">
            スクレイパーが使用する AWS Bedrock のリージョンとモデル ID を変更します。
          </Text>
          {bedrockError   && <Alert icon={<IconAlertCircle size={16} />} color="red">{bedrockError}</Alert>}
          {bedrockSuccess && <Alert color="green">設定を保存しました。</Alert>}
          <Group align="flex-end">
            <TextInput
              label="リージョン"
              placeholder="例: us-east-1"
              value={bedrockSettings.bedrock_region}
              onChange={(e) => setBedrockSettings((s) => ({ ...s, bedrock_region: e.currentTarget.value }))}
              disabled={bedrockSaving}
              w={200}
            />
            <TextInput
              label="モデル ID"
              placeholder="例: us.anthropic.claude-sonnet-4-6"
              value={bedrockSettings.bedrock_model_id}
              onChange={(e) => setBedrockSettings((s) => ({ ...s, bedrock_model_id: e.currentTarget.value }))}
              disabled={bedrockSaving}
              w={320}
            />
            <Button
              leftSection={<IconDeviceFloppy size={16} />}
              onClick={handleBedrockSave}
              loading={bedrockSaving}
              disabled={bedrockSaving || !bedrockSettings.bedrock_region.trim() || !bedrockSettings.bedrock_model_id.trim()}
              mt="xl"
            >
              保存
            </Button>
          </Group>
        </Stack>
      </Paper>

      <Divider />

      {/* ジョブ一覧（アコーディオン） */}
      <Group justify="space-between">
        <Text fw={500}>ジョブ一覧</Text>
        <Button
          variant="subtle"
          size="xs"
          leftSection={<IconRefresh size={14} />}
          onClick={() => fetchJobs(currentPage)}
          loading={loadingJobs}
        >
          更新
        </Button>
      </Group>

      {loadingJobs && <Center><Loader size="sm" /></Center>}

      {!loadingJobs && displayJobs.length === 0 && (
        <Text c="dimmed" ta="center" size="sm">ジョブ履歴はありません</Text>
      )}

      {!loadingJobs && displayJobs.length > 0 && (
        <Accordion
          multiple
          value={openedJobs}
          onChange={setOpenedJobs}
          variant="separated"
        >
          {displayJobs.map((job) => {
            const target = getJobTarget(job);
            return (
              <Accordion.Item key={job.id} value={job.id}>
                <Accordion.Control py="md">
                  <Group gap="xs" wrap="wrap">
                    <Text size="sm" fw={500}>
                      {JOB_TYPE_LABELS[job.job_type] ?? job.job_type}
                    </Text>
                    {target && (
                      <Badge
                        variant="light"
                        color="gray"
                        style={{ textTransform: 'none' }}
                      >
                        {target}
                      </Badge>
                    )}
                    <Badge
                      variant="filled"
                      color={statusColor(job.status)}
                      style={{ textTransform: 'none' }}
                    >
                      {STATUS_LABELS[job.status] ?? job.status}
                    </Badge>
                    <Text size="xs" c="dimmed">
                      {formatDate(job.created_at)}
                    </Text>
                  </Group>
                </Accordion.Control>
                <Accordion.Panel>
                  <JobLogPanel job={job} onCancel={handleCancelJob} />
                </Accordion.Panel>
              </Accordion.Item>
            );
          })}
        </Accordion>
      )}

      {totalPages > 1 && (
        <Group justify="center" mt="sm">
          <Pagination
            total={totalPages}
            value={currentPage}
            onChange={handlePageChange}
            size="sm"
          />
        </Group>
      )}
    </Stack>
  );
}

export default ScrapeControl;
